/**
 * Handlers for quantity and unit changes
 */
import { createHandlePartChange } from "./partHandlers";

/**
 * Handle quantity change
 */
export const createHandleQtyChange = ({
  text,
  selectedTextId,
  filteredUnits,
  setTexts,
  setSelectedText,
  handlePartChange,
  saveToHistory,
  isElementBeingEdited,
  setPendingChanges,
}) => {
  return (e) => {
    if (isElementBeingEdited) {
      setPendingChanges(true);
    }

    const newQtyStr = e.target.value;

    // Validate: max 4 characters and max value 9999
    if (newQtyStr === "" || /^\d+$/.test(newQtyStr)) {
      // Check max length (4 characters)
      if (newQtyStr.length > 4) {
        return; // Don't update if exceeds max length
      }

      const numericQty = parseInt(newQtyStr, 10);
      // Check max value (9999) and ensure it's a positive number
      const isValidQty = !isNaN(numericQty) && numericQty > 0 && numericQty <= 9999;

      let newUnit = text.selectedContent?.unit || "";

      if (isValidQty && !newUnit) {
        const partRef = text.selectedContent?.part_ref;
        const matchedPart = Object.values(text.partsByType || {})
          .flatMap((type) => type.parts || [])
          .find((p) => p.part_ref === partRef);
        newUnit = matchedPart?.defaultUnit || "";
      }

      if (!isValidQty) {
        newUnit = "";
      }

      const matchingUnit = filteredUnits
        .flatMap((unitType) => unitType.unit_type_for_name || [])
        .find((u) => u.unit_name === newUnit);

      const newUnitId = matchingUnit?.unit_id || "";

      setSelectedText((prev) => ({
        ...prev,
        selectedContent: {
          ...prev.selectedContent,
          qty: newQtyStr,
          unit: newUnit,
          units: newUnitId,
        },
      }));

      setTexts((prev) =>
        prev.map((t) =>
          t.id === selectedTextId
            ? {
                ...t,
                selectedContent: {
                  ...t.selectedContent,
                  qty: newQtyStr,
                  unit: newUnit,
                  units: newUnitId,
                },
              }
            : t
        )
      );

      handlePartChange("qty", newQtyStr, newUnit, newUnitId);
      saveToHistory();
    }
  };
};

/**
 * Handle unit change
 */
export const createHandleUnitChange = ({
  text,
  selectedTextId,
  filteredUnits,
  setTexts,
  setSelectedText,
  handlePartChange,
  saveToHistory,
  isElementBeingEdited,
  setPendingChanges,
}) => {
  return (e) => {
    if (isElementBeingEdited) {
      setPendingChanges(true);
    }

    const newUnitName = e.target.value;
    const matchingUnit = filteredUnits
      .flatMap((unitType) => unitType.unit_type_for_name || [])
      .find((u) => u.unit_name === newUnitName);
    const newUnitId = matchingUnit?.unit_id || "";

    setSelectedText((prev) => ({
      ...prev,
      selectedContent: {
        ...prev.selectedContent,
        unit: newUnitName,
        units: newUnitId,
      },
    }));

    setTexts((prev) =>
      prev.map((t) =>
        t.id === selectedTextId
          ? {
              ...t,
              selectedContent: {
                ...t.selectedContent,
                unit: newUnitName,
                units: newUnitId,
              },
            }
          : t
      )
    );

    handlePartChange("unit", newUnitId, newUnitName, "");
    saveToHistory();
  };
};

/**
 * Handle remove part
 */
export const createHandleRemovePart = ({
  text,
  setTexts,
  setSelectedText,
  setEditTextBoxContent,
  setShowEditNote,
  setShowEditTask,
  setShowQtyUnitOptions,
  saveToHistory,
  isElementBeingEdited,
  setPendingChanges,
  onPartRemoved, // Callback to track removed part references
}) => {
  return () => {
    if (
      !text?.selectedContent ||
      text.selectedContent.type !== "part" ||
      (!text.selectedContent.content && !text.selectedContent.part)
    ) {
      return;
    }

    if (isElementBeingEdited) {
      setPendingChanges(true);
    }

    const selected = text?.selectedContent;
    const selectedPartId = selected?.part?.id || selected.id;
    let partRemoved = false;
    let removedPartData = null;

    const updatedPartsByType = Object.fromEntries(
      Object.entries(text.partsByType || {}).map(([typeName, typeData]) => {
        const filteredParts = typeData.parts.filter((p) => {
          const isMatch = p.id === selectedPartId;
          if (isMatch) {
            partRemoved = true;
            // Capture the part data before removal for tracking
            removedPartData = {
              part_ref: p.part_ref || selected.part_ref,
              text_box_id: text.text_box_id || text.textboxId || text.textBoxId || text.canvas_text_box_id || text.canvasTextBoxId,
              text_box_content_id: p.text_box_content_id || text.text_box_content_data?.find(
                (entry) => entry.part_ref === (p.part_ref || selected.part_ref)
              )?.text_box_content_id,
              units: p.units || p.unit_id || selected.units,
              inline_note: p.notePlainText || p.note || "",
            };
            return false;
          }
          return true;
        });
        return [typeName, { ...typeData, parts: filteredParts }];
      })
    );

    if (partRemoved) {
      // Track the removed part reference if callback is provided
      if (onPartRemoved && removedPartData) {
        onPartRemoved(removedPartData);
      }

      setSelectedText((prev) => ({
        ...prev,
        partsByType: updatedPartsByType,
        selectedContent: null,
      }));

      setTexts((prev) =>
        prev.map((t) =>
          t.id === text.id
            ? { ...t, partsByType: updatedPartsByType, selectedContent: null }
            : t
        )
      );

      saveToHistory();
    }

    setEditTextBoxContent(true);
    setShowEditNote(false);
    setShowEditTask(false);
    setShowQtyUnitOptions(false);
  };
};
